/* ============================================================
 * Product card v2 — carousel + "+" add-to-cart / size modal.
 *
 * <product-card-carousel>: mobile = native scroll-snap swipe with dot
 * sync; desktop (>=990px) = cross-fade between images, advancing to
 * image 2 on hover and to any image via the dots.
 *
 * "+" button: single variant -> add straight to cart (spinner + drawer);
 * 2+ variants -> open <card-size-modal> to pick a size, then add.
 * Uses the theme's own cart render path (sections + renderContents).
 * ============================================================ */
(function () {
  const desktop = () => window.matchMedia('(min-width: 990px)').matches;

  class ProductCardCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-cardv2-track]');
      this.slides = Array.from(this.querySelectorAll('.cardv2__slide'));
      this.dots = Array.from(this.querySelectorAll('[data-cardv2-dot]'));
      this.card = this.closest('.cardv2');
      this.index = 0;
      if (this.slides.length < 2) return;

      this.dots.forEach((dot) =>
        dot.addEventListener('click', (e) => {
          e.preventDefault();
          this.goTo(parseInt(dot.dataset.cardv2Dot, 10));
        })
      );

      // Mobile: keep the dots in sync with the swipe position.
      this.track.addEventListener('scroll', () => {
        if (desktop()) return;
        clearTimeout(this._st);
        this._st = setTimeout(() => {
          const i = Math.round(this.track.scrollLeft / this.track.clientWidth);
          this.setActiveDot(i);
        }, 60);
      });

      // Desktop: hover advances to image 2, leaving returns to image 1.
      if (this.card) {
        this.card.addEventListener('mouseenter', () => { if (desktop()) this.goTo(1); });
        this.card.addEventListener('mouseleave', () => { if (desktop()) this.goTo(0); });
      }

      this.goTo(0, false);
    }

    goTo(i, smooth = true) {
      i = Math.max(0, Math.min(i, this.slides.length - 1));
      this.index = i;
      if (desktop()) {
        this.slides.forEach((s, n) => s.classList.toggle('is-active', n === i));
      } else if (this.track) {
        this.track.scrollTo({ left: i * this.track.clientWidth, behavior: smooth ? 'smooth' : 'auto' });
      }
      this.setActiveDot(i);
    }

    setActiveDot(i) {
      this.dots.forEach((d, n) => d.classList.toggle('is-active', n === i));
    }
  }
  if (!customElements.get('product-card-carousel')) {
    customElements.define('product-card-carousel', ProductCardCarousel);
  }

  /* ---- Add to cart via the theme's own drawer render ---- */
  async function addVariantToCart(variantId, btn) {
    const cart =
      document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    setBtnLoading(btn, true);
    try {
      const sectionIds =
        cart && typeof cart.getSectionsToRender === 'function'
          ? cart.getSectionsToRender().map((s) => s.id)
          : [];
      const r = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });
      const resp = await r.json();
      if (!r.ok) throw new Error(resp.description || 'add failed');
      if (cart && typeof cart.renderContents === 'function') {
        cart.classList.remove('is-empty');
        if (resp.sections) {
          cart.renderContents(resp);
        } else {
          const sections = await fetch(`/?sections=${sectionIds.join(',') || 'cart-drawer,cart-icon-bubble'}`).then((res) => res.json());
          cart.renderContents({ sections, id: variantId });
        }
      } else {
        window.location.href = (window.routes && window.routes.cart_url) || '/cart';
      }
      return true;
    } catch (e) {
      console.error('[card-v2] add failed:', e);
      return false;
    } finally {
      setBtnLoading(btn, false);
    }
  }

  function setBtnLoading(btn, on) {
    if (!btn) return;
    btn.classList.toggle('is-loading', on);
    if (on) btn.setAttribute('aria-disabled', 'true');
    else btn.removeAttribute('aria-disabled');
    const sp = btn.querySelector('.cardv2__add-spinner');
    if (sp) sp.hidden = !on;
  }

  /* ---- "+" button: direct add or size modal ---- */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('.cardv2__add');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    if (btn.getAttribute('aria-disabled') === 'true') return;
    let data;
    try {
      data = JSON.parse(btn.getAttribute('data-card-add'));
    } catch (_) {
      return;
    }
    const inStock = (data.variants || []).filter((v) => v.available !== false);
    if (data.multi && inStock.length > 1) {
      const modal = document.querySelector('card-size-modal');
      if (modal && typeof modal.open === 'function') {
        modal.open(data, btn);
        return;
      }
    }
    const id = (inStock[0] && inStock[0].id) || data.id;
    addVariantToCart(id, btn);
  });

  /* ---- Lightweight size-picker modal ---- */
  class CardSizeModal extends HTMLElement {
    connectedCallback() {
      this.titleEl = this.querySelector('[data-size-title]');
      this.kickerEl = this.querySelector('[data-size-kicker]');
      this.pillsEl = this.querySelector('[data-size-pills]');
      this.addEl = this.querySelector('[data-size-add]');
      this.selected = null;
      this.originBtn = null;

      this.querySelectorAll('[data-size-close]').forEach((el) =>
        el.addEventListener('click', () => this.close())
      );
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !this.hidden) this.close();
      });
      if (this.addEl) this.addEl.addEventListener('click', () => this.add());
    }

    open(data, originBtn) {
      this.originBtn = originBtn || null;
      this.selected = null;
      if (this.kickerEl) {
        const opt = data.option_name;
        this.kickerEl.textContent =
          opt && opt.toLowerCase() !== 'title' ? 'Select ' + opt : 'Select an option';
      }
      if (this.titleEl) this.titleEl.textContent = data.title || 'Choose an option';
      if (this.pillsEl) {
        this.pillsEl.innerHTML = '';
        (data.variants || []).forEach((v) => {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'card-size-modal__pill';
          b.textContent = v.title;
          if (v.available === false) {
            b.disabled = true;
            b.classList.add('is-soldout');
          } else {
            b.addEventListener('click', () => this.select(b, v.id));
          }
          this.pillsEl.appendChild(b);
        });
      }
      this.setAddEnabled(false);
      this.hidden = false;
      document.body.classList.add('card-size-modal-open');
    }

    select(pill, variantId) {
      this.pillsEl.querySelectorAll('.card-size-modal__pill').forEach((p) =>
        p.classList.remove('is-selected')
      );
      pill.classList.add('is-selected');
      this.selected = variantId;
      this.setAddEnabled(true);
    }

    setAddEnabled(on) {
      if (!this.addEl) return;
      this.addEl.disabled = !on;
      this.addEl.classList.toggle('is-disabled', !on);
    }

    async add() {
      if (!this.selected || this._adding) return;
      this._adding = true;
      setBtnLoading2(this.addEl, true);
      const ok = await addVariantToCart(this.selected, null);
      setBtnLoading2(this.addEl, false);
      this._adding = false;
      if (ok) this.close();
    }

    close() {
      this.hidden = true;
      document.body.classList.remove('card-size-modal-open');
      this.selected = null;
    }
  }
  if (!customElements.get('card-size-modal')) {
    customElements.define('card-size-modal', CardSizeModal);
  }

  function setBtnLoading2(btn, on) {
    if (!btn) return;
    btn.classList.toggle('is-loading', on);
    btn.toggleAttribute('aria-disabled', on);
  }
})();
